// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SharedProductPagesContexts } from "@werner/XDL/constants";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { getSettingsCollection } from "../../../App/Store/Selectors/SettingsSelectors";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;

    return {
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        isEstimatedItemCount: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
}

export const productCountStyles: ProductListProductCountStyles = {
    countText: {
        size: 18,
        weight: 500,
        css: css`
            white-space: nowrap;
        `,
    },
    itemsText: {
        size: 18,
        css: css`
            white-space: nowrap;
        `,
    },
    // XNG-changes
    queryText: {
        ellipsis: true,
        size: 28,
        weight: "bold",
        css: css`
            white-space: nowrap;
            font-weight: bold;
            margin-bottom: 15px;
        `,
    },
};

const styles = productCountStyles;

const ProductListProductQueryComponent: FC<ReturnType<typeof mapStateToProps>> = ({ totalItemCount, query }) => {
    if (!query) {
        return null;
    }

    return (
        <Typography {...styles.queryText}>{`${
            totalItemCount ? translate("Results") : translate("No results")
        } for ${query}`}</Typography>
    );
};

export const ProductListProductQuery = connect(mapStateToProps)(ProductListProductQueryComponent);

const ProductListProductCount: FC<Props> = ({ totalItemCount, query, isEstimatedItemCount }) => {
    if (!totalItemCount) {
        return null;
    }

    const itemsText = (totalItemCount !== 1 ? " Products" : "Product") + (isEstimatedItemCount ? " (estimated)" : "");
    // const itemsText = (totalItemCount !== 1 ? " products" : " product") + (query ? " for " : "");

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.countText} data-test-selector="productListCountText">
                {totalItemCount}
            </Typography>
            <Typography {...styles.itemsText}>{translate(itemsText)}</Typography>
            {/* {query && ( */}
            {/*    <Typography {...styles.queryText} data-test-selector="productListQueryText"> */}
            {/*        {query} */}
            {/*    </Typography> */}
            {/* )} */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListProductCount),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        allowedContexts: [...SharedProductPagesContexts] as any,
    },
};

export default widgetModule;
